/**
 * Client-side Cognito utilities for managing user attributes
 * 
 * These functions are designed to be called from the client-side (browser)
 * using the user's access token. No server-side admin permissions required.
 */

import {
  CognitoIdentityProviderClient,
  UpdateUserAttributesCommand,
  GetUserCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import type { SupportThread } from '../types/support-chat';
import type { CognitoUserAttribute } from '../types/cognito';

/**
 * Get user's support threads from Cognito attribute
 * 
 * @param accessToken - User's Cognito access token
 * @param attributeName - Custom attribute name (default: 'support_threads')
 * @returns Array of support threads or empty array if none found
 */
export async function getUserSupportThreads(
  accessToken: string,
  attributeName: string = 'support_threads'
): Promise<SupportThread[]> {
  try {
    const client = new CognitoIdentityProviderClient({});

    const command = new GetUserCommand({
      AccessToken: accessToken,
    });

    const response = await client.send(command);

    if (!response.UserAttributes) {
      return [];
    }

    // Find the custom attribute (custom attributes are prefixed with 'custom:')
    const fullAttributeName = attributeName.startsWith('custom:')
      ? attributeName
      : `custom:${attributeName}`;

    const attribute = response.UserAttributes.find(
      (attr) => attr.Name !== undefined && attr.Name === fullAttributeName
    );

    if (!attribute?.Value) {
      return [];
    }

    // Parse JSON array of threads
    try {
      const threads = JSON.parse(attribute.Value) as SupportThread[];
      return Array.isArray(threads) ? threads : [];
    } catch {
      // If parsing fails, return empty array
      return [];
    }
  } catch (error) {
    console.error('Error getting user support threads:', error);
    return [];
  }
}

/**
 * Update user's support threads in Cognito attribute
 * 
 * @param accessToken - User's Cognito access token
 * @param threads - Array of support threads to store
 * @param attributeName - Custom attribute name (default: 'support_threads')
 * @returns Success status
 */
export async function updateUserSupportThreads(
  accessToken: string,
  threads: SupportThread[],
  attributeName: string = 'support_threads'
): Promise<boolean> {
  try {
    const client = new CognitoIdentityProviderClient({});

    // Ensure custom attributes are prefixed with 'custom:'
    const fullAttributeName = attributeName.startsWith('custom:')
      ? attributeName
      : `custom:${attributeName}`;

    // Serialize threads array to JSON
    const threadsJson = JSON.stringify(threads);

    const command = new UpdateUserAttributesCommand({
      AccessToken: accessToken,
      UserAttributes: [
        {
          Name: fullAttributeName,
          Value: threadsJson,
        },
      ],
    });

    await client.send(command);
    return true;
  } catch (error) {
    console.error('Error updating user support threads:', error);
    return false;
  }
}

/**
 * Add a new support thread to user's thread list
 * 
 * @param accessToken - User's Cognito access token
 * @param newThread - New thread to add
 * @param attributeName - Custom attribute name (default: 'support_threads')
 * @returns Success status
 */
export async function addUserSupportThread(
  accessToken: string,
  newThread: SupportThread,
  attributeName: string = 'support_threads'
): Promise<boolean> {
  try {
    // Get existing threads
    const existingThreads = await getUserSupportThreads(accessToken, attributeName);

    // Check if thread already exists
    const threadExists = existingThreads.some(
      (thread) => thread.threadId === newThread.threadId
    );

    if (threadExists) {
      // Update existing thread
      const updatedThreads = existingThreads.map((thread) =>
        thread.threadId === newThread.threadId ? newThread : thread
      );
      return updateUserSupportThreads(accessToken, updatedThreads, attributeName);
    } else {
      // Add new thread
      const updatedThreads = [...existingThreads, newThread];
      return updateUserSupportThreads(accessToken, updatedThreads, attributeName);
    }
  } catch (error) {
    console.error('Error adding user support thread:', error);
    return false;
  }
}

/**
 * Remove a support thread from user's thread list
 * 
 * @param accessToken - User's Cognito access token
 * @param threadId - Thread ID to remove
 * @param attributeName - Custom attribute name (default: 'support_threads')
 * @returns Success status
 */
export async function removeUserSupportThread(
  accessToken: string,
  threadId: string,
  attributeName: string = 'support_threads'
): Promise<boolean> {
  try {
    // Get existing threads
    const existingThreads = await getUserSupportThreads(accessToken, attributeName);

    // Filter out the thread to remove
    const updatedThreads = existingThreads.filter(
      (thread) => thread.threadId !== threadId
    );

    return updateUserSupportThreads(accessToken, updatedThreads, attributeName);
  } catch (error) {
    console.error('Error removing user support thread:', error);
    return false;
  }
}

/**
 * Find a specific thread in user's thread list
 * 
 * @param accessToken - User's Cognito access token
 * @param threadId - Thread ID to find
 * @param attributeName - Custom attribute name (default: 'support_threads')
 * @returns Thread if found, null otherwise
 */
export async function findUserSupportThread(
  accessToken: string,
  threadId: string,
  attributeName: string = 'support_threads'
): Promise<SupportThread | null> {
  try {
    const threads = await getUserSupportThreads(accessToken, attributeName);
    return threads.find((thread) => thread.threadId === threadId) || null;
  } catch (error) {
    console.error('Error finding user support thread:', error);
    return null;
  }
}
